import React, { useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'

/** The audio side of `useShaderPreview`. */
export interface ShaderAudioState {
  connected: boolean
  /** The browser needs a click before it will play sound. */
  needsGesture: boolean
  muted: boolean
  setMuted: (muted: boolean) => void
  resume: () => void
}

interface ShaderAudioControlsProps {
  audio: ShaderAudioState | undefined
}

/** "Enable sound" when the browser is holding audio back, and a mute toggle, while audio is connected. */
export function ShaderAudioControls({ audio }: ShaderAudioControlsProps) {
  const resume = audio?.resume
  const setMuted = audio?.setMuted
  const muted = audio?.muted ?? false

  const handleEnable = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    resume?.()
  }, [resume])

  const handleMute = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setMuted?.(!muted)
  }, [setMuted, muted])

  if (!audio?.connected) return null

  return (
    <>
      {audio.needsGesture && (
        <Button variant="outline" size="sm" onClick={handleEnable} className="h-7 gap-1.5 rounded-full px-2.5 text-[11px]">
          <Icon name="volume-2" className="h-3.5 w-3.5" />
          Enable sound
        </Button>
      )}
      <Button
        variant="outline"
        size="sm"
        onClick={handleMute}
        className="h-7 w-7 rounded-md p-0"
        aria-label={muted ? 'Unmute audio' : 'Mute audio'}
        aria-pressed={muted}
        title={muted ? 'Unmute' : 'Mute'}
      >
        <Icon name={muted ? 'volume-x' : 'volume-2'} className="h-3.5 w-3.5" />
      </Button>
    </>
  )
}
